import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUserAuth } from '../../context/UserAuthContext';
import { useMechanicAuth } from '../../context/MechanicAuthContext';
import { login as loginApi } from '../../api/userAuth.api';
import { mechanicLogin } from '../../api/mechanicAuth.api';
import '../../styles/auth.css';

const ACCOUNT_TYPES = [
  { key: 'user', label: 'Customer', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg> },
  { key: 'mechanic', label: 'Mechanic', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
];

const EyeIcon = ({ open }) =>
  open ? (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

const Login = () => {
  const { login } = useUserAuth();
  const { loginMechanic } = useMechanicAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [accountType, setAccountType] = useState('user');
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || (accountType === 'mechanic' ? '/mechanic/dashboard' : '/dashboard');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTypeChange = (type) => {
    setAccountType(type);
    setError('');
    setForm({ email: '', password: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (accountType === 'user') {
        const data = await loginApi(form);
        login(data.token, data.user);
        navigate(from === '/mechanic/dashboard' ? '/dashboard' : from, { replace: true });
      } else {
        const data = await mechanicLogin(form);
        loginMechanic(data.token, data.mechanic);
        navigate('/mechanic/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const subtitle = accountType === 'mechanic'
    ? 'Sign in to your mechanic portal'
    : 'Sign in to manage your roadside services';

  return (
    <div className="auth-page" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/rsa/road-service.jpg)` }}>
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">
            <svg className="w-7 h-7" fill="none" stroke="white" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div className="auth-logo-text">RJ Road Assist</div>
          <div className="auth-logo-sub">Jaipur's #1 Roadside Assistance</div>
        </div>

        <div className="auth-type-tabs">
          {ACCOUNT_TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`auth-type-tab ${accountType === t.key ? 'active' : ''}`}
              onClick={() => handleTypeChange(t.key)}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        <h1 className="auth-title">Welcome back</h1>
        <p className="auth-subtitle">{subtitle}</p>

        {location.state?.message && !error && (
          <div className="auth-success-box" style={{ marginBottom: '0.75rem' }}>
            {location.state.message}
          </div>
        )}

        {error && (
          <div className="auth-error-box">
            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label className="auth-label">Email address</label>
            <div className="auth-input-wrap">
              <span className="auth-input-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </span>
              <input
                type="email"
                name="email"
                className="auth-input"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>
          </div>

          <div className="auth-field">
            <label className="auth-label">Password</label>
            <div className="auth-input-wrap">
              <span className="auth-input-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </span>
              <input
                type={showPass ? 'text' : 'password'}
                name="password"
                className="auth-input"
                placeholder="Enter your password"
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
              <button type="button" className="auth-eye-btn" onClick={() => setShowPass(!showPass)}>
                <EyeIcon open={showPass} />
              </button>
            </div>
          </div>

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? 'Signing in...' : `Sign In as ${accountType === 'mechanic' ? 'Mechanic' : 'Customer'}`}
          </button>
        </form>

        {accountType === 'user' && (
          <>
            <p className="auth-switch" style={{ marginTop: '1rem' }}>
              <Link to="/forgot-password">Forgot your password?</Link>
            </p>
            <p className="auth-switch" style={{ marginTop: '0.5rem' }}>
              Don't have an account? <Link to="/register">Create one</Link>
            </p>
          </>
        )}

        {accountType === 'mechanic' && (
          <>
            <p className="auth-switch" style={{ marginTop: '1rem' }}>
              <Link to="/mechanic/forgot-password">Forgot your password?</Link>
            </p>
            <p className="auth-switch" style={{ marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.8125rem' }}>
              Mechanic accounts are created by admin. Contact support if you need access.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
